import type { StepIds } from '~/components/premarketplace/partner-config';
import type { PremarketplaceFormDataSchema } from '~/provider/PremarketplaceProvider';

// Maps each answerable step to the flat `formData` field that stores its answer.
// This is the inverse of the step→field switch in
// app/routes/_app+/premarketplace+/helper.ts (transformStepsPayload), kept in one
// place so both branch evaluation (buildStepAnswers) and the stale-answer clear
// (StepProvider) agree on where a step's answer lives. Steps that capture no
// answer (introduction, eligibility-check, upgrade-profile, location,
// recommended-vendors, contact) are intentionally absent.
export const STEP_FIELD_MAP = {
  categories: 'selectedCategorySlugs',
  'bundle-categories': 'selectedBundleCategorySlug',
  intent: 'intent',
  topics: 'selectedTopics',
  // This boolean is the connect-with-vendors step's answer, so channel config can
  // branch to the support pathway when the buyer is not ready for vendor contact.
  'connect-with-vendors': 'isNotReadyToConnectWithVendors',
  // The choice on the education step ('connect' | 'not-ready') is what the vendor steps branch on,
  // and what `contact` reads to decide which treatment to show.
  education: 'educationChoice',
  'purchase-priority': 'purchasePriority',
  'contact-time': 'callTimePreference',
  'financing-interest': 'finance',
  household: 'householdSize',
} as const satisfies Partial<Record<StepIds, keyof PremarketplaceFormDataSchema>>;

// Extra fields a step owns beyond its answer. `STEP_FIELD_MAP` stays one-field-per-step because
// branch evaluation needs exactly one answer per question, but the stale-answer clear has to take
// everything the step wrote, or leaving a branch and returning re-submits the old values.
export const STEP_OWNED_FIELDS = {
  topics: ['selectedTopicLabels', 'topicsNote'],
  // Answers no question, so it is absent above — but it writes state that the nurture branch hides.
  // A stale `journey` misroutes the submission; stale ids submit vendors the buyer cannot see.
  'recommended-vendors': ['selectedVendorIds', 'journey', 'isManualVendorMatching'],
} as const satisfies Partial<Record<StepIds, readonly (keyof PremarketplaceFormDataSchema)[]>>;

// Normalise a stored answer to a set of option-value strings. Missing/empty
// answers produce an empty set (⇒ "not answered" in the evaluator).
function toStringSet(value: unknown): Set<string> {
  if (value === undefined || value === null) return new Set();
  if (Array.isArray(value)) return new Set(value.map(String));
  if (typeof value === 'string') return value === '' ? new Set() : new Set([value]);
  if (typeof value === 'boolean' || typeof value === 'number') return new Set([String(value)]);
  return new Set();
}

/**
 * Build the answers map (stepFlow.id → set of selected option-value strings)
 * that `visibleWhen` clauses are evaluated against. Only steps with a non-empty
 * answer appear.
 */
export function buildStepAnswers(formData: Partial<PremarketplaceFormDataSchema>): Map<StepIds, Set<string>> {
  const answers = new Map<StepIds, Set<string>>();
  for (const [stepId, field] of Object.entries(STEP_FIELD_MAP) as [StepIds, keyof PremarketplaceFormDataSchema][]) {
    const set = toStringSet(formData[field]);
    if (set.size > 0) answers.set(stepId, set);
  }
  return answers;
}
